import { CheckCircleRounded, ErrorRounded, ExpandMoreRounded, InfoRounded, WarningRounded } from "@mui/icons-material";
import { alpha, createTheme, lighten, type LinkProps } from "@mui/material";
import { cyan, green, grey, lightBlue, orange, red } from "@mui/material/colors";
import type {} from "@mui/x-data-grid/themeAugmentation";
import type {} from "@mui/x-date-pickers/themeAugmentation";
import type {} from "@mui/x-tree-view/themeAugmentation";
import React from "react";
import { LinkBehavior } from "../../components";

declare module "@mui/material/styles" {
  interface Palette {
    person: PaletteColor;
    role: PaletteColor;
    line: PaletteColor;
    focus: PaletteColor;
    diff: DiffPaletteOptions;
  }

  interface PaletteOptions {
    person: SimplePaletteColorOptions;
    role: SimplePaletteColorOptions;
    line: SimplePaletteColorOptions;
    focus: SimplePaletteColorOptions;
    diff: DiffPaletteOptions;
  }

  interface PaletteColor {
    opacity5?: string;
    transparent5?: string;
    opacity10?: string;
    transparent10?: string;
    transparent15?: string;
  }

  interface SimplePaletteColorOptions {
    opacity5?: string;
    transparent5?: string;
    opacity10?: string;
    transparent10?: string;
    transparent15?: string;
  }

  interface DiffPaletteOptions {
    added: string;
    removed: string;
  }

  interface TypeBackground {
    sideMenu: string;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    tertiary: true;
  }
}

declare module "@mui/material/ToggleButtonGroup" {
  interface ToggleButtonGroupProps {
    variant?: "menu" | "tertiary";
  }
}

declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides {
    person: true;
    role: true;
  }

  interface ChipPropsVariantOverrides {
    tertiary: true;
  }
}

declare module "@mui/material/Divider" {
  interface DividerPropsVariantOverrides {
    dashed: true;
  }
}

declare module "@mui/material/IconButton" {
  interface IconButtonOwnProps {
    variant?: "filled" | "tertiary";
  }
}

declare module "@mui/material/styles" {
  interface TypographyVariants {
    placeholder: React.CSSProperties;
    label: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    placeholder?: React.CSSProperties;
    label?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    placeholder: true;
    label: true;
  }
}

declare module "@mui/material/Paper" {
  interface PaperPropsVariantOverrides {
    faded: true;
  }
}

const burgundy = {
  500: "#AA2C24",
  700: "#8D1F19",
  900: "#7D1911",
};

let theme = createTheme({
  spacing: 20,
  palette: {
    background: {
      default: "#F0F3F5",
      sideMenu: "#E7E9EC",
    },
    text: {
      primary: grey[900],
      secondary: grey[600],
      disabled: alpha(grey[900], 0.3),
    },
    primary: {
      main: cyan[800],
      dark: cyan[900],
      light: cyan[600],
      opacity5: "#F2F9F9",
      transparent5: "#00838F0d",
      opacity10: "#E6F3F4",
      transparent10: "#00838F1A",
      transparent15: "#00838F26",
      contrastText: "#fff",
    },
    secondary: {
      main: orange[600],
      dark: orange[800],
      light: orange[400],
      opacity5: lighten(orange[600], 0.95),
      transparent5: alpha(orange[600], 0.05),
      opacity10: lighten(orange[600], 0.9),
      transparent10: alpha(orange[600], 0.1),
      transparent15: alpha(orange[600], 0.15),
      contrastText: "#fff",
    },
    error: {
      main: red[700],
      light: red[500],
      dark: red[900],
      contrastText: "#fff",
    },
    warning: {
      main: orange[700],
      light: orange[500],
      dark: orange[900],
      contrastText: "#fff",
    },
    info: {
      main: lightBlue[700],
      light: lightBlue[500],
      dark: lightBlue[900],
      contrastText: "#fff",
    },
    success: {
      main: green[700],
      light: green[500],
      dark: green[900],
      contrastText: "#fff",
    },
    person: {
      main: burgundy[700],
      dark: burgundy[900],
      light: burgundy[500],
      contrastText: "#fff",
    },
    role: {
      main: orange[600],
      dark: orange[800],
      light: orange[400],
      contrastText: "#fff",
    },
    focus: {
      main: cyan.A400,
    },
    line: {
      main: grey[400],
      dark: grey[600],
      light: grey[200],
    },
    diff: {
      added: "#def8de",
      removed: "#fbdfdf",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "*::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
        },
        "*::-webkit-scrollbar-track": {
          backgroundColor: "transparent",
        },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: grey[400],
          borderRadius: "8px",
        },
        "*::-webkit-scrollbar-thumb:hover": {
          backgroundColor: grey[500],
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.primary.transparent5,
          borderRadius: 8,
          boxShadow: "none",
          "&::before": {
            display: "none",
          },
        }),
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: "0px 20px",
          [theme.breakpoints.down("sm")]: {
            padding: "0px 10px",
          },
        }),
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: 0,
          paddingBottom: 20,
        },
      },
    },
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          error: <ErrorRounded />,
          warning: <WarningRounded />,
          info: <InfoRounded />,
          success: <CheckCircleRounded />,
        },
      },
      styleOverrides: {
        root: {
          alignItems: "center",
        },
        message: {
          fontWeight: 400,
        },
        colorError: ({ theme }) => ({
          backgroundColor: lighten(theme.palette.error.light, 0.8),
        }),
        colorWarning: ({ theme }) => ({
          backgroundColor: lighten(theme.palette.warning.light, 0.8),
        }),
        colorInfo: ({ theme }) => ({
          backgroundColor: lighten(theme.palette.info.light, 0.8),
        }),
        colorSuccess: ({ theme }) => ({
          backgroundColor: lighten(theme.palette.success.light, 0.8),
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          ".MuiAutocomplete-inputRoot": {
            paddingTop: 10,
            paddingLeft: 10,
            paddingBottom: 10,
          },
          ".MuiAutocomplete-input": {
            padding: "0 !important",
          },
          ".MuiFormLabel-root": {
            top: -6,
            "&.MuiInputLabel-shrink": {
              top: 0,
            },
          },
          ".MuiAutocomplete-endAdornment": {
            right: 10,
          },
        },
        paper: {
          margin: "4px 0",
          paddingLeft: 10,
          borderRadius: 10,
        },
        listbox: {
          padding: 0,
          ul: {
            marginBlockEnd: 0,
          },
          ".MuiAutocomplete-option": {
            marginRight: 10,
            paddingLeft: 10,
            paddingRight: 10,
            borderRadius: 8,
            '&[aria-disabled="true"]': {
              color: grey[500],
              opacity: "1",
            },
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: grey[600],
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {},
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: "0.875rem",
          fontWeight: "400",
          textTransform: "uppercase",
          padding: "6px 16px",
        },
      },
      defaultProps: {
        disableElevation: true,
      },
      variants: [
        {
          props: { variant: "text" },
          style: ({ theme }) => ({
            color: theme.palette.primary.main,
            textUnderlineOffset: "3px",
            "&:hover": {
              backgroundColor: "transparent",
              textDecoration: "underline",
            },
          }),
        },
        {
          props: { variant: "text", size: "small" },
          style: {
            fontSize: "0.75rem",
            fontWeight: "400",
            lineHeight: "1rem",
            textTransform: "initial",
            padding: 0,
          },
        },
        {
          props: { variant: "tertiary" },
          style: ({ theme }) => ({
            color: theme.palette.primary.main,
            fontSize: "0.875rem",
            borderRadius: 6,
            padding: "2px 10px",
            backgroundColor: theme.palette.primary.transparent5,
            "&:hover": {
              backgroundColor: theme.palette.primary.transparent15,
            },
          }),
        },
        {
          props: { variant: "tertiary", color: "error" },
          style: ({ theme }) => ({
            color: theme.palette.error.main,
            backgroundColor: alpha(theme.palette.error.main, 0.05),
            "&:hover": {
              backgroundColor: alpha(theme.palette.error.main, 0.15),
            },
          }),
        },
        {
          props: { variant: "tertiary", size: "small" },
          style: {
            fontSize: "0.625rem",
            borderRadius: 4,
            padding: "2px 6px",
          },
        },
        {
          props: { variant: "contained" },
          style: {},
        },
      ],
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          boxShadow: "1px 1px 3px rgba(0, 0, 0, 0.1)",
        },
      },
      variants: [
        {
          props: { elevation: 0 },
          style: {
            boxShadow: "none",
          },
        },
        {
          props: { variant: "faded" },
          style: {
            boxShadow: "none",
            backgroundColor: grey[100],
            borderColor: grey[200],
            borderWidth: "1px",
            borderStyle: "solid",
          },
        },
      ],
    },
    MuiCardHeader: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: "10px 20px",
          [theme.breakpoints.down("sm")]: {
            padding: "10px",
          },
          ".MuiCardHeader-action": {
            margin: 0,
          },
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.up("sm")]: {
            padding: 20,
            "&:last-child": {
              paddingBottom: 20,
            },
          },
          [theme.breakpoints.down("sm")]: {
            padding: 10,
            "&:last-child": {
              paddingBottom: 10,
            },
          },
        }),
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: 10,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: 20,
          borderRadius: 4,
          width: "fit-content",
        },
        label: {
          fontSize: ".625rem",
          fontWeight: "400",
          textTransform: "uppercase",
          paddingLeft: 8,
          paddingRight: 8,
        },
      },
      variants: [
        {
          props: { variant: "tertiary" },
          style: ({ theme }) => ({
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.opacity10,
            ".MuiChip-icon": {
              color: theme.palette.primary.main,
            },
          }),
        },
      ],
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderColor: theme.palette.line.main,
        }),
      },
      variants: [
        {
          props: { variant: "dashed" },
          style: {
            borderStyle: "dashed",
          },
        },
      ],
    },
    MuiDialog: {
      defaultProps: {
        fullWidth: true,
        slotProps: {
          backdrop: {
            style: {
              backgroundColor: "rgba(0, 0, 0, 0.1)",
              backdropFilter: "blur(4px)",
            },
          },
        },
      },
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: 12,
          [theme.breakpoints.up("md")]: {
            width: 790,
            maxWidth: "none",
          },
          overflow: "visible",
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: 20,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: 20,
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          color: "inherit",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          gap: 20,
          padding: "20px",
          ".MuiButton-root": {
            margin: 0,
          },
        },
      },
    },
    MuiIconButton: {
      variants: [
        {
          props: { variant: "filled" },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            borderRadius: "50%",
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }),
        },
        {
          props: { variant: "tertiary" },
          style: ({ theme }) => ({
            fontSize: "0.75rem",
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.transparent5,
            borderRadius: 6,
            padding: 6,
            "&:hover": {
              backgroundColor: theme.palette.primary.transparent15,
            },
          }),
        },
      ],
    },
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
        underline: "hover",
      } as LinkProps,
      styleOverrides: {
        root: {
          textUnderlineOffset: 3,
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          paddingRight: 8,
          paddingLeft: 8,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: "0px !important",
          paddingRight: 10,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          marginTop: 10,
          borderRadius: 6,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
        divider: {
          borderStyle: "dashed",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          borderColor: theme.palette.line.main,
          borderWidth: "1px",
          "&.Mui-disabled  .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(theme.palette.line.main, 0.3),
          },
          "&:hover:enabled .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.line.dark,
            borderWidth: "1px",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.focus.main,
            borderWidth: "2px",
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.error.main,
            borderWidth: "2px",
          },
        }),
      },
    },
    MuiPickersTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          ".MuiPickersInputBase-root": {
            borderRadius: 8,
            padding: 10,
          },
          ".MuiPickersSectionList-root": {
            padding: 0,
          },
          "&.Mui-focused:not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: theme.palette.focus.main,
            borderWidth: "2px",
          },
          "&.Mui-error .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: theme.palette.error.main,
            borderWidth: "2px",
          },
        }),
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: ({ theme }) => ({
          ".MuiPaper-root": {
            borderRadius: 10,
            border: `1px solid ${theme.palette.line.main}`,
            boxShadow: `2px 2px 4px ${alpha(theme.palette.common.black, 0.2)}`,
          },
        }),
      },
    },
    MuiPopper: {
      styleOverrides: {
        root: {
          ".MuiPaper-root": {
            borderRadius: 12,
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: (props) => <ExpandMoreRounded fontSize="large" {...props} />,
      },
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          borderRadius: 8,
        },
        select: {
          padding: "10px 12px",
          "&.MuiSelect-filled": {
            borderRadius: 8,
            backgroundColor: "transparent",
            padding: "4px 8px",
          },
        },
        icon: {
          color: grey[700],
        },
      },
    },
    MuiSkeleton: {
      defaultProps: {
        animation: "wave",
        height: 40,
      },
      styleOverrides: {
        text: {
          borderRadius: 8,
        },
        rectangular: {
          borderRadius: 8,
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.primary.main,
          height: 4,
          padding: "4px 0px",
        }),
        rail: ({ theme }) => ({
          color: theme.palette.line.main,
          opacity: 1,
          height: 4,
          borderRadius: 2,
        }),
        track: ({ theme }) => ({
          color: theme.palette.primary.main,
          height: 4,
          borderRadius: 2,
          borderColor: theme.palette.primary.main,
          ".Mui-disabled &": {
            color: theme.palette.line.dark,
            borderColor: theme.palette.line.dark,
          },
        }),
        mark: ({ theme }) => ({
          color: theme.palette.common.white,
        }),
        thumb: {
          height: 16,
          width: 16,
          "&:focus, &:hover, &.Mui-active": {
            boxShadow: "0 3px 6px rgba(0, 0, 0, 0.3)",
          },
        },
        valueLabel: ({ theme }) => ({
          lineHeight: 1.2,
          fontSize: 12,
          background: "unset",
          padding: 0,
          width: 32,
          height: 32,
          borderRadius: "50% 50% 50% 0",
          backgroundColor: theme.palette.primary.main,
          transformOrigin: "bottom left",
          transform: "translate(50%, -100%) rotate(-45deg) scale(0)",
          "&::before": { display: "none" },
          "&.MuiSlider-valueLabelOpen": {
            transform: "translate(50%, -100%) rotate(-45deg) scale(1)",
          },
          "& > *": {
            transform: "rotate(45deg)",
          },
        }),
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        root: {
          ".MuiSvgIcon-root": {
            fontSize: "1.2rem",
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 32,
          height: 20,
          padding: 0,
          display: "flex",
          margin: "auto",
          "&:active": {
            "& .MuiSwitch-thumb": {
              width: 16,
            },
            "& .MuiSwitch-switchBase.Mui-checked": {
              transform: "translateX(10px)",
            },
          },
        },
        thumb: {
          boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
          width: 16,
          height: 16,
          borderRadius: 8,
          transition: "width 200ms",
        },
        track: {
          borderRadius: 10,
          opacity: 1,
          backgroundColor: grey[400],
        },
        switchBase: ({ theme }) => ({
          padding: 2,
          "&.Mui-checked": {
            transform: "translateX(12px)",
            color: theme.palette.common.white,
            "& + .MuiSwitch-track": {
              opacity: 1,
              backgroundColor: theme.palette.primary.main,
            },
          },
          "& .Mui-checked": {
            transform: "translateX(9px)",
          },
        }),
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: "inherit",
          color: "inherit",
        },
        fontSizeSmall: {
          fontSize: 14,
        },
        fontSizeMedium: {
          fontSize: 18,
        },
        fontSizeLarge: {
          fontSize: 24,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: "42px",
          height: "42px",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: "42px",
          height: "42px",
          padding: "16px 20px",
          fontSize: "0.75rem",
          fontWeight: "400",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: ({ theme }) => ({
          ".MuiTableCell-root": {
            borderColor: theme.palette.line.main,
            paddingTop: 0,
            paddingBottom: 6,
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          paddingTop: 8,
          paddingBottom: 8,
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.common.white,
          borderRadius: 8,
          width: "100%",
          ".MuiInputBase-root": {
            padding: "10px 12px",
          },
          ".MuiFormHelperText-root": {
            marginLeft: 0,
          },
          ".MuiInputBase-input": {
            padding: 0,
            "&::placeholder": {
              ...theme.typography.placeholder,
              opacity: 1,
            },
          },
        }),
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          color: grey[700],
          "& .MuiSvgIcon-root": {
            color: grey[400],
          },
          "&.Mui-selected .MuiSvgIcon-root": {
            color: "inherit",
          },
          "&.Mui-disabled": {
            backgroundColor: grey[100],
            "&.Mui-selected": {
              color: grey[500],
              backgroundColor: grey[400],
            },
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      variants: [
        {
          props: { variant: "menu" },
          style: ({ theme }) => ({
            "& .MuiToggleButton-root": {
              border: "none",
              borderRadius: 8,
              justifyContent: "flex-start",
              padding: "6px 10px",
              fontWeight: 400,
              fontSize: "1rem",
              lineHeight: "1rem",
              textTransform: "none",
              color: theme.palette.text.primary,

              "&.Mui-selected": {
                color: theme.palette.primary.main,
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.common.black, 0.03),
                },
              },

              "&.Mui-disabled": {
                backgroundColor: "transparent",
                border: "none",
                color: theme.palette.text.disabled,
              },

              "&:hover": {
                backgroundColor: alpha(theme.palette.common.black, 0.03),
              },
            },
          }),
        },
        {
          props: { variant: "tertiary" },
          style: ({ theme }) => ({
            button: {
              border: "none",
              backgroundColor: theme.palette.primary.transparent5,
              "&:hover": {
                backgroundColor: theme.palette.primary.transparent10,
              },
              "&.Mui-selected": {
                backgroundColor: theme.palette.primary.transparent15,
              },
            },
          }),
        },
      ],
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          maxWidth: "500px",
          borderRadius: 10,
          padding: 10,
          backgroundColor: theme.palette.common.white,
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.line.main}`,
          boxShadow: "2px 2px 4px rgba(0, 0, 0, 0.2)",
        }),
        arrow: ({ theme }) => ({
          color: theme.palette.common.white,
          "&::before": {
            border: `1px solid ${theme.palette.line.main}`,
          },
        }),
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
        }),
        main: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
        }),
        virtualScroller: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
        }),
        columnHeaders: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
        }),
        columnHeader: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
        }),
        scrollbarFiller: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
        }),
        cell: {
          fontWeight: "normal",
        },
        footerContainer: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiTreeItem: {
      styleOverrides: {
        root: {
          ".MuiTreeItem-content": {
            gap: 10,
            paddingTop: 0,
            paddingBottom: 0,
          },
        },
      },
    },
    MuiUseMediaQuery: {
      defaultProps: {
        noSsr: true,
      },
    },
  },
  typography: {
    fontSize: 16,
    fontFamily: '"Open Sans", sans-serif',
  },
});

theme = createTheme(theme, {
  typography: {
    h1: {
      fontSize: "3rem",
      [theme.breakpoints.down("sm")]: {
        fontSize: "2.4rem",
      },
      fontWeight: "400",
      lineHeight: "3.5rem",
    },
    h2: {
      fontSize: "2.5rem",
      fontWeight: "400",
      lineHeight: "3rem",
    },
    h3: {
      fontSize: "2rem",
      [theme.breakpoints.down("sm")]: {
        fontSize: "1.5rem",
      },
      fontWeight: "700",
      lineHeight: "2.5rem",
    },
    h4: {
      fontSize: "1.5rem",
      lineHeight: "1.75rem",
      [theme.breakpoints.down("sm")]: {
        fontSize: "1rem",
        lineHeight: "1.5rem",
      },
      fontWeight: "700",
    },
    h5: {
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
      [theme.breakpoints.down("sm")]: {
        fontSize: "1rem",
        lineHeight: "1.5rem",
      },
      fontWeight: "700",
    },
    h6: {
      fontSize: "1.25rem",
      [theme.breakpoints.down("sm")]: {
        fontSize: "1rem",
      },
      fontWeight: "400",
      lineHeight: "1.75rem",
    },
    body2: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      [theme.breakpoints.down("sm")]: {
        fontSize: "0.875rem",
      },
      fontWeight: "700",
    },
    body1: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      [theme.breakpoints.down("sm")]: {
        fontSize: "0.875rem",
      },
      fontWeight: "400",
    },
    button: {
      fontSize: "0.75rem",
      fontWeight: "600",
      textTransform: "uppercase",
      color: theme.palette.primary.main,
    },
    caption: {
      fontSize: "0.75rem",
      color: grey[700],
      fontWeight: "400",
      lineHeight: "1.5rem",
    },
    overline: {
      display: "flex",
      fontSize: "0.75rem",
      fontWeight: "400",
      textTransform: "uppercase",
      lineHeight: "1rem",
      paddingBottom: "5px",
      borderBottom: `1px solid ${theme.palette.line.main}`,
      marginBottom: "5px",
    },
    label: {
      fontSize: "0.75rem",
      color: `${theme.palette.text.secondary}`,
      fontWeight: "400",
      lineHeight: "1.5rem",
      textTransform: "uppercase",
    },
    placeholder: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      [theme.breakpoints.down("sm")]: {
        fontSize: "0.875rem",
        lineHeight: "1rem",
      },
      color: `${theme.palette.text.secondary}`,
      fontWeight: "400",
    },
  },
});

export { theme };
